import React, { useEffect, useRef } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    MenuItem,
    Alert,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { ProductSearchAutocomplete } from "../common/ProductSearchAutocomplete";
import { Product } from "../../types";
import { formatCurrencyBDT } from "../../utils/currency";
import {
    ConversionRole,
    CostAllocationMethod,
} from "../../services/productConversionService";

export interface ConversionFormItem {
    product_id: string;
    product?: Product;
    role: ConversionRole;
    quantity: number;
    allocation_percentage?: number;
}

// Receiving/consuming is keyed by product_id, so two lines sharing a
// product_id within the SAME role would be ambiguous — combine them into
// one line instead.
export const hasDuplicateProductsInRole = (
    items: ConversionFormItem[],
    role: ConversionRole,
): boolean => {
    const roleItems = items.filter((i) => i.role === role);
    return roleItems.some(
        (item, index) =>
            !!item.product_id &&
            roleItems.some(
                (other, i) => i !== index && other.product_id === item.product_id,
            ),
    );
};

// A product's own OUT (as input) and blended-cost-IN (as output) in the
// same conversion has no economic meaning — see ProductConversionForm's
// blocking banner and the server-side check it mirrors.
export const hasOverlapBetweenRoles = (items: ConversionFormItem[]): boolean => {
    const inputIds = new Set(
        items.filter((i) => i.role === "INPUT").map((i) => i.product_id),
    );
    return items.some((i) => i.role === "OUTPUT" && inputIds.has(i.product_id));
};

interface OutputCost {
    unitCost: number;
    totalCost: number;
}

export interface ConversionPreview {
    inputTotal: number;
    costPool: number;
    outputCosts: Record<string, OutputCost>;
}

// Client-side mirror of api/app/services/costing.py::allocate_amount —
// same largest-remainder method, done in integer cents, so what the
// operator previews here matches what the server actually saves. Kept as
// a pure function so it's easy to eyeball against its Python counterpart.
export const calculateConversionPreview = (
    items: ConversionFormItem[],
    additionalCost: number,
    method: CostAllocationMethod,
): ConversionPreview => {
    const inputs = items.filter((i) => i.role === "INPUT");
    const outputs = items.filter((i) => i.role === "OUTPUT");

    const inputTotal =
        Math.round(
            inputs.reduce((sum, i) => {
                const cost = i.product ? Number(i.product.cost_price) : 0;
                return sum + cost * i.quantity;
            }, 0) * 100,
        ) / 100;
    const costPool = Math.round((inputTotal + (additionalCost || 0)) * 100) / 100;

    const outputCosts: Record<string, OutputCost> = {};
    if (outputs.length === 0) {
        return { inputTotal, costPool, outputCosts };
    }

    if (outputs.length === 1) {
        const o = outputs[0];
        const totalCost = costPool;
        outputCosts[o.product_id] = {
            totalCost,
            unitCost:
                o.quantity > 0 ? Math.round((totalCost / o.quantity) * 100) / 100 : 0,
        };
        return { inputTotal, costPool, outputCosts };
    }

    let weights: number[];
    if (method === "MANUAL") {
        weights = outputs.map((o) => Number(o.allocation_percentage) || 0);
    } else if (method === "WEIGHT") {
        weights = outputs.map((o) =>
            o.product?.weight ? Number(o.product.weight) * o.quantity : 0,
        );
    } else {
        weights = outputs.map((o) =>
            o.product ? Number(o.product.selling_price) * o.quantity : 0,
        );
    }

    const weightSum = weights.reduce((a, b) => a + b, 0);
    if (weightSum <= 0) {
        outputs.forEach((o) => {
            outputCosts[o.product_id] = { unitCost: 0, totalCost: 0 };
        });
        return { inputTotal, costPool, outputCosts };
    }

    const totalCents = Math.round(costPool * 100);
    const rawShares = weights.map((w) => (w / weightSum) * totalCents);
    const floors = rawShares.map((s) => Math.floor(s));
    const remainders = rawShares.map((s, i) => s - floors[i]);
    const leftover = totalCents - floors.reduce((a, b) => a + b, 0);
    const order = remainders
        .map((r, i) => ({ r, i }))
        .sort((a, b) => b.r - a.r)
        .map((x) => x.i);
    const resultCents = [...floors];
    for (let k = 0; k < leftover; k++) {
        resultCents[order[k]] += 1;
    }

    outputs.forEach((o, idx) => {
        const totalCost = resultCents[idx] / 100;
        outputCosts[o.product_id] = {
            totalCost,
            unitCost:
                o.quantity > 0 ? Math.round((totalCost / o.quantity) * 100) / 100 : 0,
        };
    });

    return { inputTotal, costPool, outputCosts };
};

interface ProductConversionFormProps {
    products: Product[];
    items: ConversionFormItem[];
    setItems: (items: ConversionFormItem[]) => void;
    costAllocationMethod: CostAllocationMethod;
    setCostAllocationMethod: (method: CostAllocationMethod) => void;
    additionalCost: number;
    setAdditionalCost: (value: number) => void;
    notes: string;
    setNotes: (value: string) => void;
}

const ROLE_LABELS: Record<ConversionRole, { title: string; helper: string }> = {
    INPUT: {
        title: "Input Products",
        helper: "Stock consumed by this conversion",
    },
    OUTPUT: {
        title: "Output Products",
        helper: "Stock produced by this conversion",
    },
};

export const ProductConversionForm: React.FC<ProductConversionFormProps> = ({
    products,
    items,
    setItems,
    costAllocationMethod,
    setCostAllocationMethod,
    additionalCost,
    setAdditionalCost,
    notes,
    setNotes,
}) => {
    const theme = useTheme();
    // Phones get one card per item — the table needs ~520px.
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const prevItemsLengthRef = useRef(items.length);
    useEffect(() => {
        prevItemsLengthRef.current = items.length;
    }, [items.length]);

    const outputs = items.filter((i) => i.role === "OUTPUT");
    const preview = calculateConversionPreview(
        items,
        additionalCost,
        costAllocationMethod,
    );
    const overlap = hasOverlapBetweenRoles(items);

    const addItem = (role: ConversionRole, product: Product | null) => {
        if (!product) return;
        setItems([
            ...items,
            { product_id: product.product_id, product, role, quantity: 1 },
        ]);
    };

    const removeItem = (index: number) => {
        setItems(items.filter((_, i) => i !== index));
    };

    const updateItem = (index: number, field: keyof ConversionFormItem, value: any) => {
        const updated = [...items];
        updated[index] = { ...updated[index], [field]: value };
        setItems(updated);
    };

    const renderRoleSection = (role: ConversionRole) => {
        const roleItems = items
            .map((item, index) => ({ item, index }))
            .filter(({ item }) => item.role === role);
        const isDupe = hasDuplicateProductsInRole(items, role);

        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {ROLE_LABELS[role].title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        {ROLE_LABELS[role].helper}
                    </Typography>

                    {isDupe && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            The same product appears on multiple{" "}
                            {role === "INPUT" ? "input" : "output"} lines — combine
                            them into a single line instead.
                        </Alert>
                    )}

                    {isMobile ? (
                        <Stack spacing={1.5}>
                            {roleItems.map(({ item, index }) => {
                                const isProductDupe =
                                    !!item.product_id &&
                                    roleItems.some(
                                        ({ item: other, index: otherIndex }) =>
                                            otherIndex !== index && other.product_id === item.product_id,
                                    );
                                const isOverlapping =
                                    role === "OUTPUT" &&
                                    items.some((i) => i.role === "INPUT" && i.product_id === item.product_id);
                                const unitCost =
                                    role === "INPUT"
                                        ? item.product
                                            ? Number(item.product.cost_price)
                                            : 0
                                        : preview.outputCosts[item.product_id]?.unitCost || 0;
                                const totalCost =
                                    role === "INPUT"
                                        ? unitCost * item.quantity
                                        : preview.outputCosts[item.product_id]?.totalCost || 0;
                                const showAllocation =
                                    role === "OUTPUT" && costAllocationMethod === "MANUAL" && outputs.length > 1;
                                return (
                                    <Paper key={index} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                        <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-start" }}>
                                            <ProductSearchAutocomplete
                                                value={item.product || null}
                                                onChange={(newValue) =>
                                                    updateItem(index, "product_id", newValue?.product_id || "")
                                                }
                                                label="Product"
                                                secondaryLine="stock"
                                                sx={{ flex: 1, minWidth: 0 }}
                                            />
                                            <IconButton
                                                color="error"
                                                onClick={() => removeItem(index)}
                                                aria-label="Remove item"
                                                sx={{ mt: 0.5 }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                        {item.product && (
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                                                {item.product.name}
                                                {role === "OUTPUT" && !item.product.stock && (
                                                    <> — new to inventory, a stock record will be created</>
                                                )}
                                            </Typography>
                                        )}
                                        {isProductDupe && (
                                            <Typography variant="caption" color="error" display="block">
                                                Already on this list — combine into one line.
                                            </Typography>
                                        )}
                                        {isOverlapping && (
                                            <Typography variant="caption" color="error" display="block">
                                                Also used as an input — a product can't be on both sides.
                                            </Typography>
                                        )}
                                        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1.5 }}>
                                            <TextField
                                                label="Quantity"
                                                type="number"
                                                size="small"
                                                value={item.quantity}
                                                onChange={(e) =>
                                                    updateItem(index, "quantity", parseInt(e.target.value) || 0)
                                                }
                                                inputProps={{ min: 1 }}
                                                sx={{ width: 96 }}
                                            />
                                            {showAllocation && (
                                                <TextField
                                                    label="Allocation %"
                                                    type="number"
                                                    size="small"
                                                    value={item.allocation_percentage ?? ""}
                                                    onChange={(e) =>
                                                        updateItem(index, "allocation_percentage", parseFloat(e.target.value) || 0)
                                                    }
                                                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                                                    sx={{ width: 110 }}
                                                />
                                            )}
                                            <Box sx={{ ml: "auto", textAlign: "right" }}>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {formatCurrencyBDT(totalCost)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {role === "OUTPUT" ? "est. " : ""}
                                                    {formatCurrencyBDT(unitCost)} each
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                );
                            })}
                            <ProductSearchAutocomplete
                                key={`${role}-${roleItems.length}`}
                                value={null}
                                onChange={(product) => addItem(role, product)}
                                label={`Search and add ${role === "INPUT" ? "input" : "output"} product`}
                                secondaryLine="stock"
                                sx={{ width: "100%" }}
                            />
                        </Stack>
                    ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Product</TableCell>
                                    <TableCell align="center">Quantity</TableCell>
                                    {role === "OUTPUT" &&
                                        costAllocationMethod === "MANUAL" &&
                                        outputs.length > 1 && (
                                            <TableCell align="center">
                                                Allocation %
                                            </TableCell>
                                        )}
                                    <TableCell align="center">
                                        {role === "INPUT" ? "Unit Cost" : "Est. Unit Cost"}
                                    </TableCell>
                                    <TableCell align="center">
                                        {role === "INPUT" ? "Total Cost" : "Est. Total Cost"}
                                    </TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {roleItems.map(({ item, index }) => {
                                    const isProductDupe =
                                        !!item.product_id &&
                                        roleItems.some(
                                            ({ item: other, index: otherIndex }) =>
                                                otherIndex !== index &&
                                                other.product_id === item.product_id,
                                        );
                                    const isOverlapping =
                                        role === "OUTPUT" &&
                                        items.some(
                                            (i) =>
                                                i.role === "INPUT" &&
                                                i.product_id === item.product_id,
                                        );
                                    const unitCost =
                                        role === "INPUT"
                                            ? item.product
                                                ? Number(item.product.cost_price)
                                                : 0
                                            : preview.outputCosts[item.product_id]
                                                  ?.unitCost || 0;
                                    const totalCost =
                                        role === "INPUT"
                                            ? unitCost * item.quantity
                                            : preview.outputCosts[item.product_id]
                                                  ?.totalCost || 0;

                                    return (
                                        <React.Fragment key={index}>
                                            <TableRow
                                                sx={
                                                    item.product
                                                        ? {
                                                              "& > td": {
                                                                  borderBottom: 0,
                                                                  paddingBottom: 0.5,
                                                              },
                                                          }
                                                        : undefined
                                                }
                                            >
                                                <TableCell>
                                                    <ProductSearchAutocomplete
                                                        value={item.product || null}
                                                        onChange={(newValue) =>
                                                            updateItem(
                                                                index,
                                                                "product_id",
                                                                newValue?.product_id || "",
                                                            )
                                                        }
                                                        label="Select product"
                                                        secondaryLine="stock"
                                                        sx={{
                                                            minWidth: {
                                                                xs: "100%",
                                                                sm: 200,
                                                            },
                                                        }}
                                                    />
                                                    {isProductDupe && (
                                                        <Typography
                                                            variant="caption"
                                                            color="error"
                                                            display="block"
                                                        >
                                                            Already on this list —
                                                            combine into one line.
                                                        </Typography>
                                                    )}
                                                    {isOverlapping && (
                                                        <Typography
                                                            variant="caption"
                                                            color="error"
                                                            display="block"
                                                        >
                                                            Also used as an input —
                                                            a product can't be on
                                                            both sides.
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        value={item.quantity}
                                                        onChange={(e) =>
                                                            updateItem(
                                                                index,
                                                                "quantity",
                                                                parseInt(e.target.value) ||
                                                                    0,
                                                            )
                                                        }
                                                        inputProps={{ min: 1 }}
                                                        sx={{ width: 80 }}
                                                    />
                                                </TableCell>
                                                {role === "OUTPUT" &&
                                                    costAllocationMethod === "MANUAL" &&
                                                    outputs.length > 1 && (
                                                        <TableCell>
                                                            <TextField
                                                                type="number"
                                                                size="small"
                                                                value={
                                                                    item.allocation_percentage ??
                                                                    ""
                                                                }
                                                                onChange={(e) =>
                                                                    updateItem(
                                                                        index,
                                                                        "allocation_percentage",
                                                                        parseFloat(
                                                                            e.target.value,
                                                                        ) || 0,
                                                                    )
                                                                }
                                                                inputProps={{
                                                                    min: 0,
                                                                    max: 100,
                                                                    step: 0.01,
                                                                }}
                                                                sx={{ width: 90 }}
                                                            />
                                                        </TableCell>
                                                    )}
                                                <TableCell align="center">
                                                    <Typography variant="body2">
                                                        {formatCurrencyBDT(unitCost)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="medium"
                                                    >
                                                        {formatCurrencyBDT(totalCost)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => removeItem(index)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                            {item.product && (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={5}
                                                        sx={{ pt: 0.5, pb: 0.5 }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {item.product.name}
                                                            {role === "OUTPUT" &&
                                                                !item.product.stock && (
                                                                    <>
                                                                        {" "}
                                                                        — new to
                                                                        inventory, a
                                                                        stock record
                                                                        will be
                                                                        created
                                                                    </>
                                                                )}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                })}

                                <TableRow>
                                    <TableCell>
                                        <ProductSearchAutocomplete
                                            key={`${role}-${roleItems.length}`}
                                            value={null}
                                            onChange={(product) => addItem(role, product)}
                                            label={`Search and add ${
                                                role === "INPUT" ? "input" : "output"
                                            } product`}
                                            secondaryLine="stock"
                                            sx={{ width: "100%", minWidth: 200 }}
                                        />
                                    </TableCell>
                                    <TableCell />
                                    {role === "OUTPUT" &&
                                        costAllocationMethod === "MANUAL" &&
                                        outputs.length > 1 && <TableCell />}
                                    <TableCell />
                                    <TableCell />
                                    <TableCell />
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                    )}
                </CardContent>
            </Card>
        );
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {overlap && (
                <Alert severity="error">
                    A product can't be both an input and an output in the same
                    conversion. Only the consumed portion of a product needs to be
                    listed as an input — stock you're leaving untouched doesn't need
                    a line at all.
                </Alert>
            )}

            {renderRoleSection("INPUT")}
            {renderRoleSection("OUTPUT")}

            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Conversion Details
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: outputs.length > 1 ? "1fr 1fr" : "1fr",
                            },
                            gap: 2,
                        }}
                    >
                        {outputs.length > 1 && (
                            <TextField
                                select
                                fullWidth
                                label="Cost Allocation Method"
                                value={costAllocationMethod}
                                onChange={(e) =>
                                    setCostAllocationMethod(
                                        e.target.value as CostAllocationMethod,
                                    )
                                }
                                helperText="How the shared input cost is split across multiple outputs"
                            >
                                <MenuItem value="SALES_VALUE">
                                    By sales value (recommended)
                                </MenuItem>
                                <MenuItem value="WEIGHT">By net weight</MenuItem>
                                <MenuItem value="MANUAL">Manual percentage</MenuItem>
                            </TextField>
                        )}
                        <TextField
                            fullWidth
                            label="Additional Cost (Optional)"
                            type="number"
                            value={additionalCost}
                            onChange={(e) =>
                                setAdditionalCost(parseFloat(e.target.value) || 0)
                            }
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{ startAdornment: "৳" }}
                            helperText="Packaging/labor cost folded into the total before it's split across outputs"
                        />
                    </Box>
                    <TextField
                        fullWidth
                        label="Notes (Optional)"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        multiline
                        rows={2}
                        sx={{ mt: 2 }}
                    />
                </CardContent>
            </Card>

            {items.length > 0 && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Cost Summary
                        </Typography>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography>Input cost:</Typography>
                            <Typography>{formatCurrencyBDT(preview.inputTotal)}</Typography>
                        </Box>
                        {additionalCost > 0 && (
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography>Additional cost:</Typography>
                                <Typography>
                                    {formatCurrencyBDT(additionalCost)}
                                </Typography>
                            </Box>
                        )}
                        <Divider sx={{ my: 1 }} />
                        <Box display="flex" justifyContent="space-between">
                            <Typography variant="h6">Total cost to allocate:</Typography>
                            <Typography variant="h6" color="primary">
                                {formatCurrencyBDT(preview.costPool)}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};
